import React, { useEffect, useState, useCallback } from 'react';
import {
  Tabs,
  Table,
  Select,
  Button,
  Spin,
  Alert,
  Space,
  message,
  Form,
} from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { PageTitle, StyledCard } from '../common';

interface Cuenta {
  codigo: string;
  denominacion: string;
}

interface TipoComprobante {
  id: number;
  nombre: string;
}

interface ItemConfig {
  item: string;
  cuenta_codigo: string | null;
  cuenta_denominacion: string | null;
}

interface RetencionConfig extends ItemConfig {
  retencion_id: number;
  retencion_descripcion: string;
  retencion_tipo: string;
  retencion_aplicar: string;
}

interface ConfigData {
  items_fijos: ItemConfig[];
  retenciones: RetencionConfig[];
}

interface ComprobanteCfgTipo {
  tipo_comprobante_con_afip: number | null;
  tipo_comprobante_sin_afip: number | null;
}

interface ComprobanteCfg {
  compra: ComprobanteCfgTipo;
  servicio: ComprobanteCfgTipo;
}

const LABEL_MAP: Record<string, string> = {
  COMPRA_BRUTO: 'Compra Algodón Bruto',
  IVA_COMPRAS: 'IVA Compras',
  PROVEEDORES_ALGODON: 'Proveedores Algodón',
  SERVICIO_DESMOTE: 'Servicio Desmote (Gravado)',
  IVA_VENTAS: 'IVA Ventas',
  DEUDORES_SERVICIOS: 'Deudores Servicios',
};

const COMPRA_FIJOS = ['COMPRA_BRUTO', 'IVA_COMPRAS', 'PROVEEDORES_ALGODON'];
const SERVICIO_FIJOS = ['SERVICIO_DESMOTE', 'IVA_VENTAS', 'DEUDORES_SERVICIOS'];

const empty: ComprobanteCfg = {
  compra: { tipo_comprobante_con_afip: null, tipo_comprobante_sin_afip: null },
  servicio: { tipo_comprobante_con_afip: null, tipo_comprobante_sin_afip: null },
};

const ConfiguracionContable: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tieneContable, setTieneContable] = useState(false);
  const [cuentas, setCuentas] = useState<Cuenta[]>([]);
  const [tiposComprobante, setTiposComprobante] = useState<TipoComprobante[]>([]);
  const [config, setConfig] = useState<ConfigData>({ items_fijos: [], retenciones: [] });
  const [selecciones, setSelecciones] = useState<Record<string, string | null>>({});
  const [comprobanteCfg, setComprobanteCfg] = useState<ComprobanteCfg>(empty);

  const idEmpresa = user?.id_empresa;
  const baseUrl = process.env.REACT_APP_LIQUIDACION_URL;

  const cargarDatos = useCallback(async () => {
    if (!idEmpresa) return;
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };

      const [verificarRes, cuentasRes, configRes, comprobanteCfgRes, tiposRes] = await Promise.all([
        axios.get(`${baseUrl}/verificar_empresa_contable/${idEmpresa}/`, { headers }),
        axios.get<Cuenta[]>(`${baseUrl}/cuentas_contables/${idEmpresa}/`, { headers }).catch(() => ({ data: [] as Cuenta[] })),
        axios.get<ConfigData>(`${baseUrl}/configuracion_contable/${idEmpresa}/`, { headers }).catch(() => ({ data: { items_fijos: [], retenciones: [] } as ConfigData })),
        axios.get<ComprobanteCfg>(`${baseUrl}/configuracion_comprobante/${idEmpresa}/`, { headers }).catch(() => ({ data: empty })),
        axios.get<TipoComprobante[]>(`${baseUrl}/tipos_comprobante/${idEmpresa}/`, { headers }).catch(() => ({ data: [] as TipoComprobante[] })),
      ]);

      const tiene = verificarRes.data.tiene_contable;
      setTieneContable(tiene);

      if (tiene) {
        setCuentas(cuentasRes.data);
        setTiposComprobante(tiposRes.data);
        setConfig(configRes.data);
        setComprobanteCfg(comprobanteCfgRes.data ?? empty);
        const inicial: Record<string, string | null> = {};
        for (const item of configRes.data.items_fijos) {
          inicial[item.item] = item.cuenta_codigo ?? null;
        }
        for (const item of configRes.data.retenciones) {
          inicial[item.item] = item.cuenta_codigo ?? null;
        }
        setSelecciones(inicial);
      }
    } catch {
      message.error('Error al cargar la configuración contable');
    } finally {
      setLoading(false);
    }
  }, [idEmpresa, baseUrl, getAccessTokenSilently]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleCuentaChange = (item: string, codigo: string | null) => {
    setSelecciones(prev => ({ ...prev, [item]: codigo ?? null }));
  };

  const handleComprobanteChange = (
    tipo: 'compra' | 'servicio',
    campo: 'tipo_comprobante_con_afip' | 'tipo_comprobante_sin_afip',
    valor: number | null
  ) => {
    setComprobanteCfg(prev => ({
      ...prev,
      [tipo]: { ...prev[tipo], [campo]: valor },
    }));
  };

  const handleGuardar = async () => {
    if (!idEmpresa) return;
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };

      const items = Object.entries(selecciones).map(([item, cuenta_codigo]) => {
        const cuentaObj = cuentas.find(c => c.codigo === cuenta_codigo);
        return {
          item,
          cuenta_codigo: cuenta_codigo ?? null,
          cuenta_denominacion: cuentaObj?.denominacion ?? null,
        };
      });

      await Promise.all([
        axios.post(`${baseUrl}/configuracion_contable/${idEmpresa}/`, { items }, { headers }),
        axios.post(`${baseUrl}/configuracion_comprobante/${idEmpresa}/`, comprobanteCfg, { headers }),
      ]);

      message.success('Configuración guardada correctamente');
    } catch {
      message.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const renderCuentaSelect = (item: string) => (
    <Select
      showSearch
      allowClear
      placeholder="Sin asignar"
      value={selecciones[item] ?? undefined}
      onChange={(val) => handleCuentaChange(item, val ?? null)}
      filterOption={(input, option) =>
        (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
      }
      style={{ width: '100%', minWidth: 280 }}
      options={cuentas.map(c => ({
        value: c.codigo,
        label: `${c.codigo} — ${c.denominacion}`,
      }))}
    />
  );

  const renderComprobanteSelect = (
    tipo: 'compra' | 'servicio',
    campo: 'tipo_comprobante_con_afip' | 'tipo_comprobante_sin_afip'
  ) => (
    <Select
      showSearch
      allowClear
      placeholder="Sin asignar"
      value={comprobanteCfg[tipo][campo] ?? undefined}
      onChange={(val) => handleComprobanteChange(tipo, campo, val ?? null)}
      filterOption={(input, option) =>
        (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
      }
      style={{ width: '100%', minWidth: 320 }}
      options={tiposComprobante.map(t => ({ value: t.id, label: t.nombre }))}
    />
  );

  const columnasFijos = [
    {
      title: 'Concepto',
      dataIndex: 'item',
      key: 'item',
      width: 220,
      render: (item: string) => LABEL_MAP[item] ?? item,
    },
    {
      title: 'Cuenta contable',
      dataIndex: 'item',
      key: 'cuenta',
      render: (item: string) => renderCuentaSelect(item),
    },
  ];

  const columnasRetenciones = [
    {
      title: 'Retención',
      dataIndex: 'retencion_descripcion',
      key: 'desc',
      width: 220,
    },
    {
      title: 'Tipo',
      dataIndex: 'retencion_tipo',
      key: 'tipo',
      width: 100,
    },
    {
      title: 'Cuenta contable',
      dataIndex: 'item',
      key: 'cuenta',
      render: (item: string) => renderCuentaSelect(item),
    },
  ];

  const fijosCompra = config.items_fijos.filter(i => COMPRA_FIJOS.includes(i.item));
  const fijosServicio = config.items_fijos.filter(i => SERVICIO_FIJOS.includes(i.item));
  const retencionesCompra = config.retenciones.filter(
    r => r.retencion_aplicar === 'COMPRA' || r.retencion_aplicar === 'COMPRA/SERVICIO'
  );
  const retencionesServicio = config.retenciones.filter(
    r => r.retencion_aplicar === 'SERVICIO' || r.retencion_aplicar === 'COMPRA/SERVICIO'
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!tieneContable) {
    return (
      <div>
        <PageTitle>Configuraciones Contables</PageTitle>
        <Alert
          type="warning"
          showIcon
          message="Esta empresa no tiene vinculación con el sistema contable."
          description="Para habilitar esta funcionalidad, la empresa debe estar registrada en el sistema contable con el mismo CUIT."
          style={{ marginTop: 24 }}
        />
      </div>
    );
  }

  const renderComprobantesCard = (tipo: 'compra' | 'servicio') => (
    <StyledCard title="Tipos de Comprobante para Asiento Contable" style={{ marginBottom: 16 }}>
      <Form layout="vertical">
        <Form.Item
          label="Comprobante con registro de AFIP (liquidación con comprobante emitido)"
          style={{ marginBottom: 12 }}
        >
          {renderComprobanteSelect(tipo, 'tipo_comprobante_con_afip')}
        </Form.Item>
        <Form.Item
          label="Comprobante sin registro de AFIP (liquidación sin comprobante emitido)"
          style={{ marginBottom: 0 }}
        >
          {renderComprobanteSelect(tipo, 'tipo_comprobante_sin_afip')}
        </Form.Item>
      </Form>
    </StyledCard>
  );

  const tabCompra = (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      {renderComprobantesCard('compra')}
      <StyledCard title="Items fijos — Compra Algodón">
        <Table
          dataSource={fijosCompra}
          columns={columnasFijos}
          rowKey="item"
          pagination={false}
          size="small"
        />
      </StyledCard>
      {retencionesCompra.length > 0 && (
        <StyledCard title="Retenciones">
          <Table
            dataSource={retencionesCompra}
            columns={columnasRetenciones}
            rowKey="item"
            pagination={false}
            size="small"
          />
        </StyledCard>
      )}
    </Space>
  );

  const tabServicio = (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      {renderComprobantesCard('servicio')}
      <StyledCard title="Items fijos — Servicio Desmote">
        <Table
          dataSource={fijosServicio}
          columns={columnasFijos}
          rowKey="item"
          pagination={false}
          size="small"
        />
      </StyledCard>
      {retencionesServicio.length > 0 && (
        <StyledCard title="Retenciones">
          <Table
            dataSource={retencionesServicio}
            columns={columnasRetenciones}
            rowKey="item"
            pagination={false}
            size="small"
          />
        </StyledCard>
      )}
    </Space>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <PageTitle>Configuraciones Contables</PageTitle>
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleGuardar}
          size="large"
        >
          Guardar
        </Button>
      </div>

      <Tabs
        defaultActiveKey="compra"
        items={[
          { key: 'compra', label: 'Compra Algodón', children: tabCompra },
          { key: 'servicio', label: 'Servicio Desmote', children: tabServicio },
        ]}
      />
    </div>
  );
};

export default ConfiguracionContable;
